import React, { useState } from 'react';

import { Button } from '@/components/ui/button';

interface DeleteConfirmRowTypedProps {
  /** The exact string the user must type to enable confirmation (profile name). */
  expected: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled: boolean;
  isDeleting: boolean;
}

/**
 * Variant of DeleteConfirmRow that requires the user to type an exact string
 * (typically the profile name) before the destructive action is enabled.
 * Used when a delete would cascade to one or more assigned assets.
 *
 * Implemented as a sibling component rather than via composition because the
 * row layout, button labels, and disabled-state logic differ enough that
 * extending DeleteConfirmRow would have required passing more props than the
 * shared structure saves. If a third typed-confirm variant is added, fold the
 * shared row chrome into a base primitive (Rule of Three).
 */
export const DeleteConfirmRowTyped: React.FC<DeleteConfirmRowTypedProps> = ({
  expected,
  onConfirm,
  onCancel,
  disabled,
  isDeleting,
}) => {
  const [typed, setTyped] = useState('');
  const isMatch = typed === expected;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-chariot-status-warning text-sm font-medium">
        Type &ldquo;{expected}&rdquo; to confirm:
      </span>
      <input
        type="text"
        value={typed}
        onChange={e => setTyped(e.target.value)}
        placeholder={expected}
        disabled={disabled}
        className="border-chariot-stroke text-chariot-text-primary placeholder:text-chariot-text-tertiary bg-chariot-background-primary h-8 rounded-md border px-2 text-sm focus:outline-none"
        aria-label={`Type ${expected} to confirm deletion`}
        data-testid="single-scan-profile-delete-typed-input"
        autoFocus
      />
      <Button
        variant="destructive"
        size="sm"
        onClick={onConfirm}
        disabled={disabled || !isMatch}
        data-testid="single-scan-profile-delete-confirm-button"
      >
        {isDeleting ? 'Deleting...' : 'Yes, delete'}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        disabled={disabled}
        data-testid="single-scan-profile-delete-cancel-button"
      >
        Cancel
      </Button>
    </div>
  );
};
